import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { Button, Chip } from "@heroui/react";
import { Spinner } from "@heroui/spinner";

import { DataTable } from "@/components/data-table";
import Footer from "@/features/Footer/Footer";
import { postAuthorized } from "@/lib/fetch";

type ActiveSession = {
  session_id: string;
  user_id: string;
  email: string;
  groups: string[];
  tableau_username: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  is_current_session: boolean;
};

const ACTIVE_SESSIONS_QUERY_KEY = ["users", "sessions", "active"] as const;

async function fetchActiveSessions(): Promise<ActiveSession[]> {
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_URL}/users/sessions/active`,
    {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch active sessions (${response.status})`);
  }

  return response.json();
}

async function revokeSession(sessionId: string): Promise<void> {
  await postAuthorized(
    `${import.meta.env.VITE_BACKEND_URL}/users/sessions/${sessionId}/revoke`,
    {},
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR");
}

function sessionTimeoutMinutes(groups: string[]): number {
  if (
    groups.length > 0 &&
    groups.every((g) => g.toLowerCase().startsWith("a7-"))
  ) {
    return 30;
  }
  return 480;
}

function isInactive(session: ActiveSession): boolean {
  const lastSeen = new Date(session.last_seen_at).getTime();
  const timeoutMs = sessionTimeoutMinutes(session.groups) * 60 * 1000;
  return Date.now() - lastSeen > timeoutMs;
}

function buildColumns(
  onRevoke: (sessionId: string) => void,
  revokingSessionId: string | undefined,
): ColumnDef<ActiveSession>[] {
  return [
    {
      accessorKey: "session_id",
      header: "Session ID",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-slate-700">
          {row.getValue("session_id") as string}
        </span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-xs text-slate-800">
          {row.getValue("email") as string}
        </span>
      ),
    },
    {
      accessorKey: "groups",
      header: "Groups",
      cell: ({ row }) => {
        const groups = row.getValue("groups") as string[];
        return (
          <span className="text-xs text-slate-700">
            {groups.join(", ") || "-"}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-xs text-slate-700">
          {formatDateTime(row.getValue("created_at") as string)}
        </span>
      ),
    },
    {
      accessorKey: "last_seen_at",
      header: "Last Seen",
      cell: ({ row }) => (
        <span className="text-xs text-slate-700">
          {formatDateTime(row.getValue("last_seen_at") as string)}
        </span>
      ),
    },
    {
      accessorKey: "expires_at",
      header: "Expires",
      cell: ({ row }) => (
        <span className="text-xs text-slate-700">
          {formatDateTime(row.getValue("expires_at") as string)}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const session = row.original;
        if (session.is_current_session) {
          return (
            <div className="text-right">
              <Chip size="sm" variant="flat" color="success">
                Current session
              </Chip>
            </div>
          );
        }

        const isRevoking = revokingSessionId === session.session_id;
        const inactive = isInactive(session);

        return (
          <div className="flex items-center justify-end gap-2">
            {inactive && (
              <Chip size="sm" variant="flat" color="warning">
                Inactif
              </Chip>
            )}
            <Button
              size="sm"
              color="danger"
              variant="solid"
              radius="sm"
              onClick={() => onRevoke(session.session_id)}
              disabled={isRevoking}
              className="min-w-20 text-white"
            >
              {isRevoking ? "Revoking..." : "Revoke"}
            </Button>
          </div>
        );
      },
    },
  ];
}

export default function SessionsActive() {
  const queryClient = useQueryClient();

  const sessionsQuery = useQuery({
    queryKey: ACTIVE_SESSIONS_QUERY_KEY,
    queryFn: fetchActiveSessions,
    refetchOnWindowFocus: false,
  });

  const revokeMutation = useMutation({
    mutationFn: revokeSession,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ACTIVE_SESSIONS_QUERY_KEY,
      });
    },
  });

  const sortedSessions = useMemo(() => {
    if (!sessionsQuery.data) return [];
    return [...sessionsQuery.data].sort((a, b) => {
      return (
        new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime()
      );
    });
  }, [sessionsQuery.data]);

  if (sessionsQuery.isLoading) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner />
      </div>
    );
  }

  if (sessionsQuery.isError) {
    return (
      <section className="px-8 py-4 font-sans font-light leading-5 text-blue-950">
        <div className="text-sm text-red-700">
          Failed to load sessions:{" "}
          {sessionsQuery.error instanceof Error
            ? sessionsQuery.error.message
            : "unknown error"}
        </div>
        <Footer />
      </section>
    );
  }

  const columns = buildColumns(
    (sessionId) => revokeMutation.mutate(sessionId),
    revokeMutation.variables,
  );

  return (
    <section className="px-8 py-4 font-sans font-light leading-5 text-blue-950">
      <DataTable<ActiveSession, unknown>
        table_name="sessions_active"
        label="Active Sessions"
        columns={columns}
        data={sortedSessions}
      />
      {revokeMutation.isError ? (
        <p className="px-4 pb-4 text-sm text-red-700">
          Failed to revoke session:{" "}
          {revokeMutation.error instanceof Error
            ? revokeMutation.error.message
            : "unknown error"}
        </p>
      ) : null}
      <Footer />
    </section>
  );
}
